import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './poster.css';
import { config } from '../config';
import { boundsOf, fitCamera } from '../design/geo';
import type { Camera, Design, Label } from '../design/schema';
import { elevationProfile, statsLine } from '../design/stats';
import { getStyle } from '../design/styles';
import { fitPadding, posterLayout } from './layout';
import logoBlack from './img/logo-black.svg';
import logoWhite from './img/logo-white.svg';

const ROUTE_SOURCE = 'tm-route';
const ROUTE_LAYER = 'tm-route-line';
const DEFAULT_VIEW: Camera = { center: [6.7089, 46.1792], zoom: 8.6, bearing: 0 };

export interface PosterProps {
  design: Design;
  /** Editor mode: the map can be moved, labels dragged, empty text shows a hint. */
  interactive?: boolean;
  /** Holds the map still (no drag or pinch) while the editor's view is zoomed in, so a drag scrolls the view instead. */
  locked?: boolean;
  /** Next tap on the map drops a label. */
  placing?: boolean;
  selectedLabel?: string | null;
  onCamera?: (camera: Camera) => void;
  onAddLabel?: (lng: number, lat: number) => void;
  onSelectLabel?: (id: string | null) => void;
  onMoveLabel?: (id: string, lng: number, lat: number) => void;
  /** Fires once map tiles and fonts have settled: the poster is complete on screen. */
  onReady?: () => void;
}

export interface PosterHandle {
  /** The view currently on screen (the fitted view when design.camera is null). */
  getCamera: () => Camera;
}

/** The camera a design resolves to at a given map viewport size. */
export function resolveCamera(design: Design, mapWidth: number, mapHeight: number): Camera {
  if (design.camera) return design.camera;
  const bounds = boundsOf(design.routes.map((r) => r.coords));
  if (!bounds) return DEFAULT_VIEW;
  return { ...fitCamera(bounds, mapWidth, mapHeight, fitPadding(design.elevation)), bearing: 0 };
}

function routeData(design: Design): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: design.routes.map((r) => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: r.coords.map((c) => [c[0], c[1]]) },
    })),
  };
}

/** The rounded axis maximum Chart.js picks for a 100px-tall linear scale starting at 0 (v1's profile). */
function niceMax(max: number): number {
  if (!(max > 0)) return 1;
  const nice = (range: number) => {
    const exp = Math.floor(Math.log10(range));
    const f = range / 10 ** exp;
    return (f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10) * 10 ** exp;
  };
  const maxSpaces = 6;
  let step = nice(max / maxSpaces);
  if (Math.ceil(max / step) > maxSpaces) step = nice((Math.ceil(max / step) * step) / maxSpaces);
  return Math.ceil(max / step) * step;
}

/** Filled elevation profile, measured from sea level exactly as v1's chart was. */
function profilePath(samples: number[]): string {
  const top = niceMax(Math.max(...samples));
  const pts = samples.map((e, i) => `${((i / (samples.length - 1)) * 1000).toFixed(1)},${(100 - (Math.max(0, e) / top) * 100).toFixed(2)}`);
  return `M0,100 L${pts.join(' L')} L1000,100 Z`;
}

const Poster = forwardRef<PosterHandle, PosterProps>(function Poster(
  { design, interactive = false, locked = false, placing = false, selectedLabel = null, onCamera, onAddLabel, onSelectLabel, onMoveLabel, onReady },
  ref,
) {
  const layout = posterLayout(design.product);
  const style = getStyle(design.style);
  const routeColor = design.line.color ?? style.route;

  const glRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const styleUrlRef = useRef(style.mapbox);
  // map.isStyleLoaded() is false whenever tiles are in flight, so track style readiness ourselves.
  const styleReadyRef = useRef(false);
  const userMoveRef = useRef(false);
  const draggingRef = useRef<string | null>(null);

  // Latest props for map event handlers, which are bound once.
  const latest = useRef({ design, routeColor, placing, onCamera, onAddLabel, onSelectLabel, onMoveLabel, onReady });
  latest.current = { design, routeColor, placing, onCamera, onAddLabel, onSelectLabel, onMoveLabel, onReady };

  const camera = resolveCamera(design, layout.map.width, layout.map.height);

  useImperativeHandle(ref, () => ({
    getCamera: () => {
      const map = mapRef.current;
      if (!map) return camera;
      const c = map.getCenter();
      return { center: [c.lng, c.lat], zoom: map.getZoom(), bearing: map.getBearing() };
    },
  }));

  /** Move every label and endpoint to where its coordinate currently is on the map. */
  const place = () => {
    const map = mapRef.current;
    const overlay = overlayRef.current;
    if (!map || !overlay) return;
    for (const el of overlay.querySelectorAll<HTMLElement>('.tmp-point')) {
      if (el.dataset.id && el.dataset.id === draggingRef.current) continue;
      const p = map.project([Number(el.dataset.lng), Number(el.dataset.lat)]);
      el.style.transform = `translate(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px)`;
    }
  };

  const syncRoute = () => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { design: d, routeColor: color } = latest.current;
    const data = routeData(d);
    const source = map.getSource(ROUTE_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (source) source.setData(data);
    else map.addSource(ROUTE_SOURCE, { type: 'geojson', data, tolerance: 0.1 });
    if (!map.getLayer(ROUTE_LAYER)) {
      map.addLayer({
        id: ROUTE_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': color, 'line-width': d.line.width },
      });
    } else {
      map.setPaintProperty(ROUTE_LAYER, 'line-color', color);
      map.setPaintProperty(ROUTE_LAYER, 'line-width', d.line.width);
    }
  };

  // Create the map once.
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: glRef.current!,
      accessToken: config.mapboxToken,
      style: styleUrlRef.current,
      projection: 'mercator',
      center: camera.center,
      zoom: camera.zoom,
      bearing: camera.bearing,
      interactive,
      attributionControl: false,
      preserveDrawingBuffer: true,
      fadeDuration: 0,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      maxPitch: 0,
      trackResize: false,
      renderWorldCopies: true,
    });
    mapRef.current = map;
    // Lets the capture code (services/capture.ts) reach the map without a React handle.
    (glRef.current as HTMLDivElement & { __tmMap?: mapboxgl.Map }).__tmMap = map;
    if (interactive) {
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
    }

    map.on('style.load', () => {
      styleReadyRef.current = true;
      syncRoute();
    });
    map.on('move', place);
    map.on('movestart', (e) => {
      if ((e as { originalEvent?: Event }).originalEvent) userMoveRef.current = true;
    });
    map.on('moveend', () => {
      if (!userMoveRef.current) return;
      userMoveRef.current = false;
      const c = map.getCenter();
      latest.current.onCamera?.({ center: [c.lng, c.lat], zoom: map.getZoom(), bearing: map.getBearing() });
    });
    map.on('click', (e) => {
      if (latest.current.placing) latest.current.onAddLabel?.(e.lngLat.lng, e.lngLat.lat);
      else latest.current.onSelectLabel?.(null);
    });

    // Ready = fonts loaded and the map has nothing left to load or draw.
    let ready = false;
    let fontsReady = false;
    const checkReady = () => {
      if (ready || !fontsReady || !map.loaded()) return;
      ready = true;
      place();
      latest.current.onReady?.();
    };
    (document.fonts?.ready ?? Promise.resolve()).then(() => {
      fontsReady = true;
      checkReady();
    });
    map.on('idle', checkReady);

    return () => {
      mapRef.current = null;
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Style
  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleUrlRef.current === style.mapbox) return;
    styleUrlRef.current = style.mapbox;
    styleReadyRef.current = false;
    map.setStyle(style.mapbox);
  }, [style.mapbox]);

  // Route geometry and line appearance
  useEffect(() => {
    syncRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.routes, design.line.width, routeColor]);

  // Poster shape (size / framing)
  useLayoutEffect(() => {
    mapRef.current?.resize();
  }, [layout.width, layout.height]);

  // Locked: no drag, pinch, wheel or double-tap moves the map. Taps (placing, selecting) still work.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !interactive) return;
    for (const h of [map.dragPan, map.touchZoomRotate, map.scrollZoom, map.doubleClickZoom]) {
      if (locked) h.disable();
      else h.enable();
    }
    if (!locked) map.touchZoomRotate.disableRotation();
  }, [locked, interactive]);

  // Camera: follow the design unless the map is already there (i.e. the change came from the map itself).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    const same =
      Math.abs(c.lng - camera.center[0]) < 1e-7 &&
      Math.abs(c.lat - camera.center[1]) < 1e-7 &&
      Math.abs(map.getZoom() - camera.zoom) < 1e-6 &&
      Math.abs(map.getBearing() - camera.bearing) < 1e-4;
    if (!same) map.jumpTo({ center: camera.center, zoom: camera.zoom, bearing: camera.bearing });
  }, [camera.center[0], camera.center[1], camera.zoom, camera.bearing, layout.width, layout.height]);

  // Keep overlay points in position after every render (labels added, moved, etc).
  useLayoutEffect(place);

  // Shrink the title to fit on one line.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const fit = () => {
      // v1 sizes: 48px, 32px over 19 characters, 24px over 28.
      const length = design.text.title.length;
      let size = length > 28 ? 24 : length > 19 ? 32 : 48;
      el.style.fontSize = `${size}px`;
      // Safety net v1 lacked: never let a title run past the frame.
      for (let pass = 0; pass < 6 && el.scrollWidth > el.clientWidth && size > 14; pass++) {
        size = Math.max(14, Math.floor(((size * el.clientWidth) / el.scrollWidth) * 10) / 10 - 0.2);
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    let cancelled = false;
    document.fonts?.ready.then(() => !cancelled && fit());
    return () => {
      cancelled = true;
    };
  }, [design.text.title, layout.width]);

  // --- label dragging ------------------------------------------------------
  const startDrag = (e: React.PointerEvent<HTMLElement>, label: Label) => {
    if (!interactive) return;
    e.preventDefault();
    e.stopPropagation();
    const map = mapRef.current;
    const overlay = overlayRef.current;
    const el = e.currentTarget.closest<HTMLElement>('.tmp-point');
    if (!map || !overlay || !el) return;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);

    // The poster may be CSS-scaled, so convert screen px to poster px.
    const toMapPx = (ev: { clientX: number; clientY: number }) => {
      const rect = overlay.getBoundingClientRect();
      const s = rect.width / overlay.offsetWidth || 1;
      return { x: (ev.clientX - rect.left) / s, y: (ev.clientY - rect.top) / s };
    };
    const origin = map.project([label.lng, label.lat]);
    const start = toMapPx(e);
    let moved = false;
    let pos = { x: origin.x, y: origin.y };

    const onMove = (ev: PointerEvent) => {
      const p = toMapPx(ev);
      const dx = p.x - start.x;
      const dy = p.y - start.y;
      if (!moved && Math.hypot(dx, dy) < 3) return;
      moved = true;
      draggingRef.current = label.id;
      el.classList.add('is-dragging');
      pos = {
        x: Math.max(0, Math.min(overlay.offsetWidth, origin.x + dx)),
        y: Math.max(0, Math.min(overlay.offsetHeight, origin.y + dy)),
      };
      el.style.transform = `translate(${pos.x.toFixed(2)}px, ${pos.y.toFixed(2)}px)`;
    };
    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      el.classList.remove('is-dragging');
      draggingRef.current = null;
      latest.current.onSelectLabel?.(label.id);
      if (moved) {
        const ll = map.unproject([pos.x, pos.y]);
        latest.current.onMoveLabel?.(label.id, ll.lng, ll.lat);
      }
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  const profile = useMemo(() => {
    if (!design.elevation) return null;
    const samples = elevationProfile(design.routes);
    return samples ? profilePath(samples) : null;
  }, [design.elevation, design.routes]);

  const stats = statsLine(design);
  const first = design.routes[0]?.coords[0];
  const lastRoute = design.routes[design.routes.length - 1];
  const last = lastRoute?.coords[lastRoute.coords.length - 1];
  const hint = (value: string, placeholder: string) =>
    value ? { className: '', text: value } : { className: 'is-placeholder', text: interactive ? placeholder : '' };
  // Empty fields show v1's placeholder wording in the editor. They are hints: never printed.
  const title = hint(design.text.title, 'Title of your route');
  const subtitle = hint(design.text.subtitle, 'Location or sub-title');
  const statsText = hint(stats, 'XXX KM // X,XXX METRES ELEVATION // XX:XX:XX MOVING TIME');
  const left = hint(design.text.left, 'Small message');
  const right = hint(design.text.right, 'xxx message');

  const cssVars = {
    width: layout.width,
    height: layout.height,
    '--tmp-bg': style.bg,
    '--tmp-ink': style.ink,
    '--tmp-frame': style.frame,
    '--tmp-route': routeColor,
    '--tmp-elevation': style.elevation,
    '--tmp-label': style.label,
  } as React.CSSProperties;

  return (
    <div className={`tmp${interactive ? ' is-interactive' : ''}${placing ? ' is-placing' : ''}`} style={cssVars} data-poster>
      <div className="tmp-map">
        <div className="tmp-gl" ref={glRef} />
        {profile && (
          <svg className="tmp-profile" viewBox="0 0 1000 100" preserveAspectRatio="none" aria-hidden="true">
            <path d={profile} fill={style.elevation} />
          </svg>
        )}
        <div className="tmp-overlay" ref={overlayRef}>
          {design.line.endpoints && first && last && (
            <>
              <div className="tmp-point" data-lng={first[0]} data-lat={first[1]}>
                <span className="tmp-endpoint" />
              </div>
              <div className="tmp-point" data-lng={last[0]} data-lat={last[1]}>
                <span className="tmp-endpoint is-finish" />
              </div>
            </>
          )}
          {design.labels.map((l) => {
            if (!l.text && !interactive) return null;
            const side = l.dot ? l.side : 'center';
            return (
              <div
                key={l.id}
                className={`tmp-point tmp-label side-${side}${l.id === selectedLabel ? ' is-selected' : ''}${l.text ? '' : ' is-empty'}`}
                data-id={l.id}
                data-lng={l.lng}
                data-lat={l.lat}
              >
                {l.dot && side !== 'center' && <span className="tmp-label-dot" onPointerDown={(e) => startDrag(e, l)} />}
                <span className="tmp-label-text" style={l.dx ? { marginLeft: l.dx } : undefined} onPointerDown={(e) => startDrag(e, l)}>
                  {l.text || 'Add your text'}
                </span>
              </div>
            );
          })}
        </div>
        <img className="tmp-logo" src={style.logo === 'black' ? logoBlack : logoWhite} alt="" />
      </div>
      <div className="tmp-panel">
        <div className={`tmp-stats ${statsText.className}`}>{statsText.text}</div>
        <div className={`tmp-title ${title.className}`} ref={titleRef}>
          {title.text}
        </div>
        <div className={`tmp-subtitle ${subtitle.className}`}>{subtitle.text}</div>
        <div className="tmp-footer">
          <span className={left.className}>{left.text}</span>
          <span className={right.className}>{right.text}</span>
        </div>
      </div>
    </div>
  );
});

export default Poster;
